import type { FC } from 'react';
import { useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { PapiDeviation } from '@wix/da-papi-types';
import { Url } from '@wix/da-url';
import { formatNumber } from '@wix/da-shared-react/pkg/utils/formatting';
import DeviationThumb from '../../_partials/DeviationThumb';
import SelectableListItem from '../SelectableListItem';
import BaseListCell from '../../items/BaseListItem/BaseListCell';
import DeviationTitle from '../../_partials/DeviationTitle';
import DeviationActions from '../../../tableActions/DeviationActions';
import DeviationInfo from '../../_partials/DeviationInfo';
import StatItem from '../../_partials/StatItem';
import PcpPriceEdit from '../../_partials/PcpPriceEdit';
import { StudioDeviationListContext } from '../../context';

import s from './PcpListItem.scss';

interface Props {
  deviation: PapiDeviation;
  offset: number;
  selected: number[];
  onSelect: (index: number, isSelected: boolean, isShiftKey?: boolean) => void;
}

const PcpListItem: FC<Props> = ({
  deviation,
  offset,
  selected,
  onSelect,
}) => {
  const { t } = useTranslation();
  const { actionsColRef, actionsColWidth } = useContext(
    StudioDeviationListContext
  );

  const downloadCount = deviation.pcp?.salesCount ?? 0;
  const viewCount = deviation.stats?.views ?? 0;

  // We use the first row as a probe to determine the size of columns
  const shouldAddColumnRefs = offset === 0;

  return (
    <SelectableListItem offset={offset} selected={selected} onSelect={onSelect}>
      <BaseListCell>
        <DeviationThumb deviation={deviation} />
      </BaseListCell>
      <BaseListCell className={s['cell-title']}>
        <DeviationTitle deviation={deviation} showSaleIndicators={false} />
        <DeviationInfo deviation={deviation} />
      </BaseListCell>
      <BaseListCell>
        <PcpPriceEdit deviation={deviation} />
      </BaseListCell>
      <BaseListCell>
        <StatItem
          title={t('studio.products.pcp.stats.downloadsCount')}
          value={formatNumber(downloadCount)}
          linkTo={
            downloadCount > 0
              ? Url.customerDashboard(deviation.pcp?.subproductId)
              : undefined
          }
        />
      </BaseListCell>
      <BaseListCell>
        <StatItem
          title={t('studio.products.pcp.stats.viewsCount')}
          value={formatNumber(viewCount)}
        />
      </BaseListCell>
      <BaseListCell
        ref={shouldAddColumnRefs ? actionsColRef : undefined}
        overflowHidden
      >
        <DeviationActions
          deviation={deviation}
          offset={offset}
          columnWidth={actionsColWidth}
        />
      </BaseListCell>
    </SelectableListItem>
  );
};
PcpListItem.displayName = 'PcpListItem';

export default PcpListItem;
